import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pick, prop } from 'ramda';
import { componentService } from '@core/js/services/component.service';
import { useAppSelector, useDeviceSelector, useGameConfigurationSelector, useUserSelector } from '@core/js/store/hooks';
import { useTranslations } from '@core/js/hooks/i18n';
import { useNavigate } from '@theme/js/hooks/navigate';
import { useGetRedirectLaunchUrl } from '@core/js/hooks/gameLauncher';
import { useLoginPopup } from '@theme/js/components/popups/LoginPopup';
import { setFullSizeDimensions, setFullScreenDimensions } from './helpers';
import { Loader } from '@core/js/components/common/Loader';
import { Image } from '@core/js/components/common/Image';

import style from './GameIframe.scss';

export const GameIframe = (props) => {
    const { gamecode, mobilegameformat } = props;

    const { games, isGamesLoaded } = useGameConfigurationSelector(pick(['games', 'isGamesLoaded']));
    const { isMobile, os, isLandscape } = useDeviceSelector(pick(['isMobile', 'os', 'isLandscape', 'browserName']));
    const depositPage = useAppSelector(prop('depositPage'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    const navigate = useNavigate();
    const showLoginPopup = useLoginPopup();
    const getRedirectLaunchUrl = useGetRedirectLaunchUrl();
    const t = useTranslations('playtech.mobile.system.iframe-game.');

    const [isLoading, setIsLoading] = useState(true);
    const [isGameLaunched, setIsGameLaunched] = useState(false);
    const [bgImgUrl, setBgImgUrl] = useState();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [iframeUrl, setIframeUrl] = useState('');
    const [isWrongConfiguration, setIsWrongConfiguration] = useState(false);

    const gameWrapperRef = useRef(null);
    const topGameControlsRef = useRef(null);
    const iframeRef = useRef(null);

    const game = games[gamecode];
    const name = game?.name;
    const guestDemoSupport = game?.demoModeSupport === 'guest' || game?.demoModeSupport === 'both';

    const launchGame = useCallback(() => {
        setIsLoading(true);

        (async () => {
            try {
                const launcherUrl = await getRedirectLaunchUrl({
                    gameCode: gamecode,
                    mode: isLoggedIn ? 1 : 0 // 1- Real mode, 0 - Demo mode.
                });

                setIframeUrl(launcherUrl);
                setIsGameLaunched(true);
            } catch (e) {
                setIsWrongConfiguration(true);
                setIsGameLaunched(false);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [gamecode, getRedirectLaunchUrl, isLoggedIn]);

    const expandIframe = () => {
        document.addEventListener('hotKey:escape', collapseIframe);
        gameWrapperRef?.current?.classList.toggle(style.fullScreen, true);
        document.body.style.overflow = 'hidden';
        setIsFullScreen(true);
    };

    const collapseIframe = () => {
        document.removeEventListener('hotKey:escape', collapseIframe);
        gameWrapperRef?.current?.classList.toggle(style.fullScreen, false);
        document.body.style.overflow = 'unset';
        setIsFullScreen(false);
    };

    const navigateToDepositPage = () => {
        navigate(depositPage);
    };

    const handleLoginPopup = () => {
        if (isMobile && os !== 'ios') {
            collapseIframe();
        }
        showLoginPopup();
    };

    useEffect(() => {
        const skip =
            !iframeRef.current ||
            !gameWrapperRef.current ||
            isFullScreen && !topGameControlsRef.current;

        if (skip) {
            return;
        }

        if (isMobile) {
            const setDimensions = !gameWrapperRef.current.requestFullscreen
                ? setFullSizeDimensions
                : setFullScreenDimensions;

            setTimeout(() => {
                setDimensions(
                    mobilegameformat,
                    isLandscape,
                    isFullScreen,
                    gameWrapperRef.current,
                    topGameControlsRef.current,
                    iframeRef.current
                );
            }, 200);
            return;
        }

        setTimeout(() => {
            iframeRef.current.style.height = !isFullScreen
                ? '100%'
                : `${window.innerHeight - topGameControlsRef.current.offsetHeight}px`;
            iframeRef.current.style.top = !isFullScreen
                ? 0
                : `${topGameControlsRef.current.offsetHeight}px`;
        }, 200);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        topGameControlsRef.current,
        gameWrapperRef.current,
        isFullScreen,
        isLandscape,
        window.innerHeight,
        window.innerWidth
    ]);

    useEffect(() => {
        if (!game) { return; }

        const getImageURL = (layoutKey, imageType) => game?.gameTileAssetsLandscape?.[layoutKey]?.[imageType];

        const imageURL = isMobile
            ? getImageURL('layout2x1', 'mobileImage') || getImageURL('layout1x1', 'mobileImage')
            : getImageURL('layout2x1', 'mainImage') || getImageURL('layout1x1', 'mainImage');

        setBgImgUrl(imageURL || game.defaultGameImage || '');

        setIsLoading(false);
    }, [game, isMobile]);

    useEffect(() => {
        if (!isLoggedIn || !gamecode || !game) {
            return;
        }

        launchGame();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, gamecode, game]);

    useEffect(() => {
        if (!isGamesLoaded) { return; }

        setIsWrongConfiguration(!gamecode || !game);
        setIsLoading(false);
    }, [gamecode, isGamesLoaded, game]);

    useEffect(() => () => {
        collapseIframe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location]);

    return (
        <div className={style.container}>
            <div className={style.gameWrapper} ref={gameWrapperRef}>
                {isWrongConfiguration &&
                    <div className={style.overlay}>
                        <p className={style.overlayTitle}>{t('wrong-configuration')}</p>
                    </div>
                }
                <>
                    {isFullScreen && (
                        <div
                            className={`${style.gameControls} ${style.gameControlsFullScreen}`}
                            ref={topGameControlsRef}
                        >
                            <button className={style.btnSecondary} onClick={collapseIframe}>
                                {t('close')}
                            </button>
                            {isLoggedIn ? (
                                <button
                                    className={style.btnPrimary}
                                    onClick={navigateToDepositPage}
                                >
                                    {t('deposit')}
                                </button>
                            ) : (
                                <button className={style.btnPrimary} onClick={handleLoginPopup}>
                                    {t('play-real-money')}
                                </button>
                            )}
                        </div>
                    )}
                    {isLoading && <Loader className={style.loader} />}
                    {!isLoading && !isGameLaunched && !isWrongConfiguration && (
                        <>
                            {guestDemoSupport ? (
                                <div onClick={launchGame} className={style.playBtn} />
                            ) : (
                                <div className={style.overlay}>
                                    <p className={style.overlayTitle}>{t('demo.unavailable')}</p>
                                </div>
                            )}
                            <Image alt={name} src={bgImgUrl} className={style.bgImg} />
                        </>
                    )}
                    <iframe
                        className={`${style.iframe} fn-game-iframe`}
                        ref={iframeRef}
                        src={iframeUrl}
                        allowFullScreen={true}
                    ></iframe>
                </>
            </div>
            {!isFullScreen && (
                <div className={style.gameControls}>
                    <button className={style.btnSecondary} onClick={expandIframe}>
                        {t('expand')}
                    </button>
                    {isLoggedIn ? (
                        <button className={style.btnPrimary} onClick={navigateToDepositPage}>
                            {t('deposit')}
                        </button>
                    ) : (
                        <button className={style.btnPrimary} onClick={showLoginPopup}>
                            {t('play-real-money')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

componentService.registerWebContentComponents({ GameIframe });
